/**
 * Phase D — React hook: fetch engagement summary for one or more ClickLinks.
 *
 * Uses the same useQuery + http-client pattern as the rest of the Outrena
 * frontend (TanStack Query v5 — @tanstack/react-query ^5.x).
 * Bearer token is injected automatically by the http axios interceptor —
 * callers do NOT pass a token.
 */

import { useQuery } from '@tanstack/react-query';
import { http } from '@/services/apiClient';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EngagementRecord {
  id: string;
  clickLinkId: string;
  sequenceId: string | null;
  source: 'ga4' | 'beacon';
  visibleSeconds: number | null;
  maxScrollPct: number | null;
  sessionCount: number;
  reportDate: string | null;
  engagedAt: string | null;
  updatedAt: string;
}

export interface EngagementSummary {
  clickLinkId: string;
  isEngaged: boolean;
  totalVisibleSeconds: number;
  totalSessions: number;
  maxScrollPct: number | null;
  sources: string[];
  records: EngagementRecord[];
}

// ── API fetchers ──────────────────────────────────────────────────────────────

/**
 * Fetch engagement summary for a single ClickLink.
 * Returns null if the link has no engagement data yet (HTTP 404).
 */
async function fetchClickLinkEngagement(
  clickLinkId: string,
): Promise<EngagementSummary | null> {
  try {
    return await http.get<EngagementSummary>(`/api/v1/click-engagement/${clickLinkId}`);
  } catch (err: unknown) {
    // 404 means no data yet — not an error
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) return null;
    }
    throw err;
  }
}

/**
 * Fetch all engagement summaries for a Sequence's ClickLinks.
 */
async function fetchSequenceEngagement(
  sequenceId: string,
): Promise<EngagementSummary[]> {
  return http.get<EngagementSummary[]>(`/api/v1/click-engagement/by-sequence/${sequenceId}`);
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Hook: engagement summary for one ClickLink.
 *
 * @param clickLinkId  UUID of the ClickLink to query
 * @param enabled      Set false to pause fetching (e.g. when modal is closed)
 */
export function useClickLinkEngagement(
  clickLinkId: string | null | undefined,
  enabled = true,
) {
  return useQuery<EngagementSummary | null, Error>({
    queryKey: ['click-engagement', 'link', clickLinkId],
    queryFn: () => fetchClickLinkEngagement(clickLinkId!),
    enabled: !!clickLinkId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — GA4 data updates nightly
    retry: 1,
  });
}

/**
 * Hook: all engagement summaries for a Sequence.
 *
 * @param sequenceId  UUID of the Sequence
 */
export function useSequenceEngagement(
  sequenceId: string | null | undefined,
) {
  return useQuery<EngagementSummary[], Error>({
    queryKey: ['click-engagement', 'sequence', sequenceId],
    queryFn: () => fetchSequenceEngagement(sequenceId!),
    enabled: !!sequenceId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

// ── Utility helpers ───────────────────────────────────────────────────────────

/**
 * Returns true if any of the engagement summaries show isEngaged = true.
 */
export function isSequenceEngaged(summaries: EngagementSummary[]): boolean {
  return summaries.some((s) => s.isEngaged);
}

/**
 * Returns the total visible seconds across all ClickLinks in a Sequence.
 */
export function totalVisibleSeconds(summaries: EngagementSummary[]): number {
  return summaries.reduce((acc, s) => acc + s.totalVisibleSeconds, 0);
}

/**
 * Human-readable label for dwell time.
 * Examples: "< 10 sec", "45 sec", "2 min 10 sec"
 */
export function formatDwellTime(seconds: number): string {
  if (seconds < 10) return '< 10 sec';
  if (seconds < 60) return `${seconds} sec`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m} min ${s} sec` : `${m} min`;
}
